import { Box, Text } from "@mantine/core"
import { ReactNode } from "react"
import { useSelector } from "react-redux"
import { IGeneral } from "../../../store/General"
import {
  DeliveryStatusBadge, InvoiceStatusBadge, PurchaseTypeBadge,
} from "../../../components/order/status-badges"
import RenderText from "../../../components/ui/render-text"
import { IOrderPlus } from "../../../models/Order"
import { currencyFormat } from "../../../utils/currency"
import { dateTimeSecFormat } from "../../../utils/date"
import { renderBusinessName } from "../../../utils/render-business-name"
import { renderName } from "../../../utils/render-name"
import { orderStatusPill, paymentStatusPill } from "./StatusPill"

export type CellCtx = { typeName: (code?: string | null) => string | undefined }

export type ColumnDef = {
  id: string
  /** i18n key in the supplierSales namespace */
  label: string
  /** Backend sort key; columns without one are not sortable. */
  sort?: string
  align?: "right"
  /** Hidden by default, switched on from the «Багана» menu. */
  optional?: boolean
  render: (r: IOrderPlus, ctx: CellCtx) => ReactNode
}

const dash = (v: ReactNode) => <RenderText text={v as string} />

/**
 * Default columns follow Ord_SalesList (order, type, status, customer, delivery date, amount, payment, PO);
 * the former 39-column table survives as optional columns.
 */
export const SALES_COLUMNS: ColumnDef[] = [
  {
    id: "order", label: "sales.columns.order", sort: "createdAt",
    render: (r) => (
      <Box>
        <Text ff="monospace" fw={700} fz={14}>{r.salesCode ?? "-"}</Text>
        <Text fz={14} c="gray.8">{dateTimeSecFormat(r.createdAt)}</Text>
      </Box>
    ),
  },
  { id: "type", label: "sales.type", render: (r, c) => dash(c.typeName(r.type)) },
  { id: "status", label: "sales.columns.status", sort: "orderStatus", render: (r) => orderStatusPill(r.orderStatus) },
  {
    id: "customer", label: "sales.columns.customer", sort: "receiverBusinessRefCodeProfileName",
    render: (r) => (
      <Box>
        <Text fz={14} fw={600}>{renderBusinessName(r.receiverBusiness, "") || "-"}</Text>
        <Text fz={14} c="gray.8" ff="monospace">#{r.receiverBusiness?.partner?.refCode || r.receiverBusiness?.refCode || "-"}</Text>
      </Box>
    ),
  },
  { id: "deliveryDate", label: "sales.columns.deliveryDate", sort: "deliveryDate", render: (r) => dash(r.deliveryDate ? dateTimeSecFormat(r.deliveryDate) : null) },
  { id: "amount", label: "sales.columns.amount", sort: "totalAmount", align: "right", render: (r) => <Text fz={14} fw={700} ff="monospace" ta="right" miw="max-content">{currencyFormat(r.totalAmount || 0)}</Text> },
  {
    id: "payment", label: "sales.columns.payment", sort: "paymentStatus",
    render: (r) => paymentStatusPill(r.invoice?.paymentStatus),
  },
  {
    id: "po", label: "sales.columns.po", sort: "purchaseCode",
    render: (r) => (
      <Box>
        <Text ff="monospace" fz={14} fw={600}>{r.purchaseCode || "-"}</Text>
        {r.purchaseCode && <PoStatusText code={r.orderStatus} />}
      </Box>
    ),
  },
  { id: "regNumber", label: "sales.regNumber", optional: true, sort: "receiverBusinessRegNumber", render: (r) => dash(r.receiverBusiness?.regNumber) },
  { id: "branch", label: "sales.branch", optional: true, sort: "receiverBranchName", render: (r) => dash(r.receiverBranch?.name) },
  {
    id: "receiverStaff", label: "sales.staff", optional: true, sort: "receiverStaffFirstNamePhone",
    render: (r) => (
      <Box>
        <RenderText text={renderName(r.receiverStaff)} />
        <RenderText text={r.receiverStaff?.phone} />
      </Box>
    ),
  },
  { id: "region", label: "sales.region", optional: true, render: (r) => dash(r.region?.name) },
  { id: "direction", label: "sales.direction", optional: true, render: (r) => dash(r.direction?.name) },
  {
    id: "deliveryStatus", label: "sales.deliveryStatus", optional: true, sort: "deliveryNoteStatus",
    render: (r) => <DeliveryStatusBadge code={r.deliveryNote?.deliveryNoteStatus} sla={r.deliveryNote?.sla as never} />,
  },
  {
    id: "courier", label: "sales.deliveryStaff", optional: true, sort: "deliveryStaffFirstNameEmail",
    render: (r) => (
      <Box>
        <RenderText text={renderName(r.deliveryNote?.staff)} />
        <RenderText text={r.deliveryNote?.staff?.email} />
      </Box>
    ),
  },
  { id: "deliveredDate", label: "sales.deliveredDate", optional: true, sort: "deliveryNoteDeliveryDate", render: (r) => dash(r.deliveryNote?.deliveryDate ? dateTimeSecFormat(r.deliveryNote.deliveryDate) : null) },
  { id: "paidAmount", label: "sales.paidAmount", optional: true, sort: "paidAmount", align: "right", render: (r) => <RenderText text={r.paidAmount || 0} isAmount fontWeight={600} /> },
  { id: "amountToPay", label: "sales.amountToPay", optional: true, sort: "amountToPay", align: "right", render: (r) => <RenderText text={r.amountToPay || 0} isAmount fontWeight={600} /> },
  { id: "paymentTerm", label: "sales.paymentTerm", optional: true, sort: "paymentTermDescription", render: (r) => dash(r.paymentTerm?.description) },
  { id: "invoiceNo", label: "sales.invoiceNo", optional: true, render: (r) => dash(r.invoice?.refCode) },
  { id: "invoiceStatus", label: "sales.invoiceStatus", optional: true, render: (r) => <InvoiceStatusBadge code={r.invoice?.invoiceStatus} /> },
  { id: "poType", label: "sales.poType", optional: true, sort: "purchaseType", render: (r) => <PurchaseTypeBadge code={r.purchaseType} /> },
  { id: "respondedDate", label: "sales.poDate", optional: true, sort: "respondedDate", render: (r) => dash(r.respondedDate ? dateTimeSecFormat(r.respondedDate) : null) },
  { id: "orderedUser", label: "sales.ordered", optional: true, sort: "orderedUserFirstName", render: (r) => dash(renderName(r.orderedUser)) },
  { id: "reviewedUser", label: "sales.reviewed", optional: true, render: (r) => dash(renderName(r.reviewedUser)) },
]

export const DEFAULT_COLUMN_IDS = SALES_COLUMNS.filter((c) => !c.optional).map((c) => c.id)

/** Хэсэгчлэн хүргэх tab: the same table, without the payment and PO columns (the split API does not carry them). */
export const SPLIT_COLUMN_IDS = ["order", "type", "status", "customer", "deliveryDate"]

function PoStatusText({ code }: Readonly<{ code: string | null | undefined }>) {
  const catalog = useSelector((s: { general: IGeneral }) => s.general.orderStatus)
  const label = catalog.find((c) => c.code === code)?.sentName
  return <Text fz={14} c="gray.8">{label ?? "-"}</Text>
}
